import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ThemeProvider from "@/context/ThemeProvider";
import { DialogProvider } from "@/context/DialogProvider";
import { SiteProvider } from "@/context/SiteContext";
import Login from "@/pages/Login";
import FirstLogin from "@/pages/FirstLogin";
import Onboarding from "@/pages/OnboardingWizard";
import AppShell from "@/pages/AppShell";
import Dashboard from "@/pages/Dashboard";
import LogReading from "@/pages/LogReading";
import Deviations from "@/pages/Deviations";
import Sites from "@/pages/Sites";
import Equipment from "@/pages/Equipment";
import EquipmentScanLanding from "@/pages/EquipmentScanLanding";
import MemberProfile from "@/pages/MemberProfile";
import CCPs from "@/pages/CCPs";
import Knowledge from "@/pages/Knowledge";
import Reports from "@/pages/Reports";
import RawDataEntry from "@/pages/RawDataEntry";
import Documentation from "@/pages/Documentation";
import AuditVault from "@/pages/AuditVault";
import FSMSTeam from "@/pages/FSMSTeam";
import Emergency from "@/pages/Emergency";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import BrandSettings from "@/pages/BrandSettings";
import MyReadings from "@/pages/MyReadings";
import DailyTracking from "@/pages/DailyTracking";
import CriticalTasks from "@/pages/CriticalTasks";
import FoodPrep from "@/pages/FoodPrep";
import TrainingComs from "@/pages/TrainingComs";
import Tenants from "@/pages/Tenants";
import AdminFoodPrep from "@/pages/AdminFoodPrep";
import PrinterLabel from "@/pages/PrinterLabel";
import Tiers from "@/pages/Tiers";
import FeatureRollout from "@/pages/FeatureRollout";
import TenantFeaturesAdmin from "@/pages/TenantFeaturesAdmin";
import HotColdHold from "@/pages/HotColdHold";
import AdminHotColdHold from "@/pages/AdminHotColdHold";
import Forms from "@/pages/Forms";
import DemoSetup from "@/pages/DemoSetup";
import ChecklistTemplates from "@/pages/ChecklistTemplates";
import AdminVisitorChecklist from "@/pages/AdminVisitorChecklist";
import AdminVehicleCleanChecklist from "@/pages/AdminVehicleCleanChecklist";
import VehicleScan from "@/pages/VehicleScan";
import VisitorRegistration from "@/pages/VisitorRegistration";
import PublicVisitorForm from "@/pages/PublicVisitorForm";
import CleaningProtocols from "@/pages/CleaningProtocols";
import Leads from "@/pages/Leads";
import AuditLog from "@/pages/AuditLog";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import Protected from "@/components/Protected";

// Stable role-allowlist references so <Protected allow={...}> props don't trigger
// re-renders on every App render.
const SUPERVISOR_PLUS = ["owner", "manager", "supervisor"];
const STAFF_PLUS = ["owner", "manager", "supervisor", "staff"];
const OWNER_MANAGER_SUPERADMIN = ["owner", "manager", "super_admin"];
const SUPER_ADMIN_ONLY = ["super_admin"];
const OWNER_ONLY = ["owner"];

/**
 * Redirect helper that PRESERVES the query string from the incoming URL.
 * Plain `<Navigate to="/...?tab=x">` drops anything the user/scanner appended
 * (e.g. `&facility=…&action=log`), which silently breaks QR deep-links.
 */
function NavigateWithQuery({ to, replace = true }) {
  const { search } = useLocation();
  if (!search) return <Navigate to={to} replace={replace} />;
  // Merge: target's own params win, but incoming query is appended.
  const [path, targetQuery = ""] = to.split("?");
  const merged = new URLSearchParams(targetQuery);
  const incoming = new URLSearchParams(search);
  incoming.forEach((v, k) => { if (!merged.has(k)) merged.set(k, v); });
  return <Navigate to={`${path}?${merged.toString()}`} replace={replace} />;
}

function Root() {
  const { user, tenant } = useAuth();
  if (user === null) return <div className="p-6">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_password) return <Navigate to="/change-password" replace />;
  // Brand-new owners that haven't finished the onboarding wizard go
  // straight into it. The wizard itself bounces back to /app once the
  // tenant is fully set up.
  if (user.role === "owner" && user.tenant_id && tenant && tenant.onboarding_completed !== true) {
    return <Navigate to="/onboarding" replace />;
  }
  // Staff no longer have a dashboard view — they land directly on Daily
  // Tracking. All other roles keep the dashboard as their home.
  if (user.role === "staff") return <Navigate to="/app/critical-tasks" replace />;
  return <Navigate to="/app" replace />;
}

// Index ("/app") landing. Staff don't get the dashboard — bounce them to
// Critical Tasks so their action hub is the home screen.
function HomeLanding() {
  const { user } = useAuth();
  if (user && user.role === "staff") return <Navigate to="/app/critical-tasks" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <DialogProvider>
            <SiteProvider>
            <Toaster richColors position="top-right" closeButton />
            <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/v/:siteId" element={<PublicVisitorForm />} />
            <Route path="/login" element={<Login />} />
            <Route path="/first-login" element={<FirstLogin />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<HomeLanding />} />
              <Route path="log" element={<LogReading />} />
              <Route path="my-readings" element={<MyReadings />} />
              <Route path="daily-tracking" element={<DailyTracking />} />
              <Route path="critical-tasks" element={<Protected allow={STAFF_PLUS}><CriticalTasks /></Protected>} />
              <Route path="food-prep" element={<FoodPrep />} />
              {/* Back-compat: old direct URLs redirect into the merged page with the right tab pre-selected. Query string (facility, action, etc.) is preserved so QR deep-links still work. */}
              <Route path="daily-hygiene" element={<NavigateWithQuery to="/app/daily-tracking?tab=hygiene" />} />
              <Route path="daily-cleanliness" element={<NavigateWithQuery to="/app/daily-tracking?tab=cleanliness" />} />
              <Route path="deviations" element={<Protected allow={SUPERVISOR_PLUS}><Deviations /></Protected>} />
              <Route path="hot-cold-hold" element={<Protected allow={STAFF_PLUS} features={["cooking-record", "hot-cold-hold", "thawing", "metal-detector"]}><HotColdHold /></Protected>} />
              <Route path="sites" element={<Protected allow={STAFF_PLUS}><Sites /></Protected>} />
              <Route path="fsms-team" element={<Protected allow={STAFF_PLUS}><FSMSTeam /></Protected>} />
              <Route path="training-coms" element={<Protected allow={SUPERVISOR_PLUS} module="training-coms" features={["training-coms"]}><TrainingComs /></Protected>} />
              <Route path="emergency" element={<Protected allow={STAFF_PLUS} module="emergency"><Emergency /></Protected>} />
              <Route path="equipment" element={<Protected allow={STAFF_PLUS}><Equipment /></Protected>} />
              <Route path="forms" element={<Protected allow={STAFF_PLUS} features={["forms"]}><Forms /></Protected>} />
              <Route path="visitor-registration" element={<Protected allow={["owner", "manager", "supervisor"]} features={["visitor-registration"]}><VisitorRegistration /></Protected>} />
              <Route path="vehicle-scan/:eqId" element={<Protected><VehicleScan /></Protected>} />
              <Route path="scan/equipment/:id" element={<Protected><EquipmentScanLanding /></Protected>} />
              <Route path="ccps" element={<Protected allow={STAFF_PLUS}><CCPs /></Protected>} />
              <Route path="knowledge" element={<Protected allow={SUPERVISOR_PLUS} module="knowledge"><Knowledge /></Protected>} />
              <Route path="reports" element={<Protected allow={SUPERVISOR_PLUS}><Reports /></Protected>} />
              <Route path="raw-data" element={<Protected allow={SUPERVISOR_PLUS}><RawDataEntry /></Protected>} />
              <Route path="documentation" element={<Protected allow={SUPERVISOR_PLUS} module="documentation"><Documentation /></Protected>} />
              <Route path="audit-vault" element={<Protected allow={SUPERVISOR_PLUS}><AuditVault /></Protected>} />
              <Route path="users" element={<Protected allow={OWNER_MANAGER_SUPERADMIN}><Users /></Protected>} />
              <Route path="users/:id" element={<Protected allow={SUPERVISOR_PLUS}><MemberProfile /></Protected>} />
              <Route path="me" element={<Protected><MemberProfile selfService /></Protected>} />
              <Route path="tenants" element={<Protected allow={SUPER_ADMIN_ONLY}><Tenants /></Protected>} />
              <Route path="food-prep-config" element={<Protected allow={SUPER_ADMIN_ONLY}><AdminFoodPrep /></Protected>} />
              <Route path="printer-labels" element={<Protected allow={SUPER_ADMIN_ONLY}><PrinterLabel /></Protected>} />
              <Route path="tiers" element={<Protected allow={SUPER_ADMIN_ONLY}><Tiers /></Protected>} />
              <Route path="feature-rollout" element={<Protected allow={SUPER_ADMIN_ONLY}><FeatureRollout /></Protected>} />
              <Route path="tenant-features" element={<Protected allow={SUPER_ADMIN_ONLY}><TenantFeaturesAdmin /></Protected>} />
              <Route path="hot-cold-hold-config" element={<Protected allow={SUPER_ADMIN_ONLY}><AdminHotColdHold /></Protected>} />
              <Route path="demo-setup" element={<Protected allow={SUPER_ADMIN_ONLY}><DemoSetup /></Protected>} />
              <Route path="checklist-templates" element={<Protected allow={SUPER_ADMIN_ONLY}><ChecklistTemplates /></Protected>} />
              <Route path="visitor-reg-config" element={<Protected allow={SUPER_ADMIN_ONLY}><AdminVisitorChecklist /></Protected>} />
              <Route path="vehicle-clean-config" element={<Protected allow={SUPER_ADMIN_ONLY}><AdminVehicleCleanChecklist /></Protected>} />
              <Route path="cleaning-protocols" element={<Protected allow={SUPER_ADMIN_ONLY}><CleaningProtocols /></Protected>} />
              <Route path="leads" element={<Protected allow={SUPER_ADMIN_ONLY}><Leads /></Protected>} />
              <Route path="audit-log" element={<Protected allow={SUPER_ADMIN_ONLY}><AuditLog /></Protected>} />
              <Route path="brand-settings" element={<Protected allow={SUPER_ADMIN_ONLY}><BrandSettings /></Protected>} />
              <Route path="settings" element={<Protected allow={["owner", "manager", "supervisor"]}><Settings /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </SiteProvider>
          </DialogProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
